import { z } from 'zod';
import { stampedBody } from '../common/row-version.js';
import { LanguageSchema } from '../common/language.js';
import { ImportFormatSchema } from '../import-export/envelopes.js';
// Request schemas for zones — the top of the per-project site hierarchy, a
// project-owned reference-data vocabulary (CONVENTIONS §Entity categories)
// managed under `reference_data:manage`. Project-scoped only
// (`/api/projects/:projectId/zones/*`); a zone never moves between projects.
//
// `name` .max(32) matches zones.name varchar(32); CI-unique per project
// (uq_zones_name_ci). Zones are NOT reorderable — there is no `display_order`
// input or column (dropped at the genesis baseline); the list sorts
// alphabetically or by column. At-most-one default per project
// (uq_zones_default_per_project): `is_default` may be set at create (clears any
// prior default in the same tx) and otherwise flips via the dedicated
// set-default action — it is not a PATCH field.
// ── GET /api/projects/:projectId/zones — the list under a named RULE ────────
// The zones and the clusters are CONTAINERS of the site tree, so their lists
// answer two audiences under two named rules (the surface names its rule; the
// server applies it — ADR-078 §6's grammar):
//   `manage` (the default) — the admin pages and the scope editor: an admin or a
//     `reference_data:manage` holder sees every row; a `users:manage` holder
//     their own-scope rows (a giver sees only what they hold whole); anyone
//     else is refused.
//   `reach` — ANY member: the containers holding something their scope reaches
//     (the naming rule, ADR-026 §A — a place is named by reach, its record opened
//     by a key); a filter's source on every page.
// One row shape for both.
export const CONTAINER_LIST_RULES = ['manage', 'reach'];
export const ContainerListRuleSchema = z.enum(CONTAINER_LIST_RULES);
export const ZonesListQuerySchema = z
    .object({
    rule: ContainerListRuleSchema.default('manage'),
})
    .strict();
// ── POST /api/projects/:projectId/zones ─────────────────────────────────────
export const CreateZoneRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(32),
    is_default: z.boolean().default(false),
})
    .strict();
// ── PATCH /api/projects/:projectId/zones/:zoneId ────────────────────────────
// Only `name` is mutable here: `is_default` flips via the set-default route,
// `is_disabled` via disable/reactivate. A single required field, not a sparse patch.
export const UpdateZoneRequestSchema = z
    .object({
    ...stampedBody,
    name: z.string().trim().min(1).max(32),
})
    .strict();
// ── GET /api/projects/:projectId/zones/export — current-view export ─────────
// Mirrors the list's URL view-state so the file IS what the user sees (ADR-045).
// `status` arrives comma-separated (the list status filter); `q` is the free-text
// search; `lang` drives header/sheet localization. Gated on `reference_data:manage`.
const csvFilter = z.preprocess((v) => (typeof v === 'string' ? v.split(',').map((s) => s.trim()).filter(Boolean) : v), z.array(z.string().max(64)).max(50).optional());
export const ExportZonesQuerySchema = z
    .object({
    format: ImportFormatSchema.default('xlsx'),
    lang: LanguageSchema.default('en'),
    status: csvFilter,
    q: z.string().trim().max(100).optional(),
})
    .strict();
// (The zone-assignments import row schema is DYNAMIC — one ref_list column per
// responsibility, the coverage matrix — so it is built per request by
// core/accountability's assignmentMatrix, not declared here; ADR-059.)
// ── GET …/zones/assignments/export — current chains, self-bounding to the zones
// the actor can see (dual-audience read). `q` filters by zone name. ──────────────
export const ExportZoneAssignmentsQuerySchema = z
    .object({
    format: ImportFormatSchema.default('xlsx'),
    lang: LanguageSchema.default('en'),
    q: z.string().trim().max(100).optional(),
})
    .strict();
// GET …/zones/export/combined — both zones datasets in ONE workbook (XLSX only;
// ADR-059). The service re-asserts each dataset's own export gate.
export const ZonesCombinedDatasetSchema = z.enum(['definitions', 'assignments']);
export const ExportZonesCombinedQuerySchema = ExportZonesQuerySchema.omit({ format: true })
    .extend({
    datasets: z.preprocess((v) => (typeof v === 'string' ? v.split(',').map((s) => s.trim()).filter(Boolean) : v), z.array(ZonesCombinedDatasetSchema).min(1).max(2)),
})
    .strict();
